import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { connect } from 'react-redux'
import Styles from './ConnectorsStatusCard.module.css'
import { Colors } from 'Theme'
import PropTypes from 'prop-types'
import { ChartComponent, IFText, IFSkeleton } from 'Components'
import AnalyticsSelectors from 'Stores/Analytics/Selectors'
import AnalyticsActions from 'Stores/Analytics/Actions'
import StationActions from 'Stores/Station/Actions'
import { useTranslation } from 'react-i18next'
import InfinityEnums from 'Enums/InfinityEnums'
import RequestState from 'Enums/RequestState'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { stationsFilter } from 'Constants/Filters'

const StyledHyperLink = styled.a`
  font-size: 14;
  vertical-align: middle;
  text-align: center;
  padding-bottom: 2px;
  margin-left: 6px;
  color: ${(props) => props.textColor};
  &:hover {
    color: ${(props) => props.hoverColor} !important;
  }
  &:focus {
    color: ${(props) => props.hoverColor} !important;
  }
`

const ConnectorsStatusCard = forwardRef(
  (
    {
      connectorsStatus,
      fetchConnectorsStatus,
      fetchConnectorsStatusRequestState,
      refreshComponentRef,
      setStationsFilter,
    },
    ref,
  ) => {
    const navigate = useNavigate()
    const { t } = useTranslation()

    const handleNavigate = (filterIndex, filterValue) => (e) => {
      e.preventDefault()
      const updated = [...stationsFilter]
      updated[filterIndex] = {
        ...updated[filterIndex],
        value: filterValue,
      }
      setStationsFilter(updated)
      navigate('/sites', { state: { fromAnalytics: true } })
    }

    const [series, setSeries] = useState([
      Number(connectorsStatus?.data?.connectorStatusCounts.available) || 0,
      connectorsStatus?.data?.connectorStatusCounts.occupied || 0,
      connectorsStatus?.data?.connectorStatusCounts.charging || 0,
      connectorsStatus?.data?.connectorStatusCounts.unavailable || 0,
      connectorsStatus?.data?.connectorStatusCounts.faulted || 0,
      connectorsStatus?.data?.connectorStatusCounts.offline || 0,
    ])

    useEffect(() => {
      fetchConnectorsStatus()
    }, [])

    useEffect(() => {
      if (fetchConnectorsStatusRequestState !== RequestState.LOADING)
        refreshComponentRef?.current?.setLoadingState(false)
    }, [fetchConnectorsStatusRequestState])

    const fetchHandler = () => fetchConnectorsStatus()

    useImperativeHandle(ref, () => ({
      fetchHandler,
    }))

    useEffect(() => {
      setSeries([
        connectorsStatus?.data?.connectorStatusCounts.available || 0,
        connectorsStatus?.data?.connectorStatusCounts.occupied || 0,
        connectorsStatus?.data?.connectorStatusCounts.charging || 0,
        connectorsStatus?.data?.connectorStatusCounts.unavailable || 0,
        connectorsStatus?.data?.connectorStatusCounts.faulted || 0,
        connectorsStatus?.data?.connectorStatusCounts.offline || 0,
      ])
    }, [connectorsStatus.data])

    const labels = [
      InfinityEnums.Analytics.AVAILABLE,
      InfinityEnums.Analytics.OCCUPIED,
      InfinityEnums.Analytics.CHARGING,
      InfinityEnums.Analytics.UNAVAILABLE,
      InfinityEnums.Analytics.FAULTED,
      InfinityEnums.Analytics.OFFLINE,
    ]

    const options = {
      chart: { type: 'donut' },
      labels,
      colors: [
        Colors.Analytics.Available,
        Colors.Analytics.Occupied,
        Colors.Analytics.Charging,
        Colors.Analytics.Unavailable,
        Colors.Analytics.Faulted,
        Colors.Analytics.Offline,
      ],
      legend: { show: false },
      stroke: {
        width: 3,
        colors: [Colors.white],
        lineCap: 'round',
      },
      plotOptions: {
        pie: {
          expandOnClick: false,
          donut: {
            size: '80%',
            labels: {
              show: true,
              total: {
                show: true,
                label: t('Analytics.TotalConnectors'),
                fontSize: '14px',
                color: Colors.Analytics.AnalyticsDataGreyText,
                formatter: () => connectorsStatus?.data?.totalEVSEs || 0,
              },
            },
          },
        },
      },
      dataLabels: { enabled: false },
      states: { hover: { filter: { type: 'none' } } },
      tooltip: { fillSeriesColor: false },
    }

    useEffect(() => {
      const errorHandler = (e) => {
        if (
          e.message.includes(
            'ResizeObserver loop completed with undelivered notifications' ||
              'ResizeObserver loop limit exceeded',
          )
        ) {
          const resizeObserverErr = document.getElementById(
            'webpack-dev-server-client-overlay',
          )
          if (resizeObserverErr) resizeObserverErr.style.display = 'none'
        }
      }
      window.addEventListener('error', errorHandler)
      return () => window.removeEventListener('error', errorHandler)
    }, [])

    return (
      <div style={{ backgroundColor: Colors.white }} className={Styles.Card}>
        {fetchConnectorsStatusRequestState === RequestState.LOADING ? (
          <div className={Styles.GraphContainer}>
            <IFSkeleton
              className={Styles.ChartSkeleton}
              variant="circular"
              height={'90%'}
              width={200}
            />
            <div className={Styles.LegendContainer}>
              {[...Array(2)].map((_, i) => (
                <div className={Styles.LegendDiv} key={i}>
                  {[...Array(3)].map((_, j) => (
                    <div
                      className={j === 2 ? Styles.Last : Styles.Legend}
                      key={j}
                    >
                      <IFSkeleton variant="text" height="1rem" width="7rem" />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className={Styles.GraphContainer}>
            <ChartComponent
              key={series.join('-')}
              options={options}
              series={series}
              height="100%"
              width="70%"
              type="donut"
            />
            <div className={Styles.LegendContainer}>
              <div className={Styles.LegendDiv}>
                <div className={Styles.Legend}>
                  <div
                    className={Styles.Box}
                    style={{ backgroundColor: Colors.Analytics.Available }}
                  />
                  <StyledHyperLink
                    hoverColor={Colors.primary}
                    textColor={Colors.text}
                    onClick={handleNavigate(2, ['Available'])}
                  >
                    <IFText>{InfinityEnums.Analytics.AVAILABLE}</IFText>
                  </StyledHyperLink>
                  <IFText>
                    {connectorsStatus?.data?.connectorStatusCounts.available}
                  </IFText>
                </div>
                <div className={Styles.Legend}>
                  <div
                    className={Styles.Box}
                    style={{ backgroundColor: Colors.Analytics.Unavailable }}
                  />
                  <StyledHyperLink
                    hoverColor={Colors.primary}
                    textColor={Colors.text}
                    onClick={handleNavigate(2, ['Unavailable'])}
                  >
                    <IFText>{InfinityEnums.Analytics.UNAVAILABLE}</IFText>
                  </StyledHyperLink>
                  <IFText>
                    {connectorsStatus?.data?.connectorStatusCounts.unavailable}
                  </IFText>
                </div>

                <div className={Styles.Last}>
                  <div
                    className={Styles.Box}
                    style={{ backgroundColor: Colors.Analytics.Charging }}
                  />
                  <StyledHyperLink
                    hoverColor={Colors.primary}
                    textColor={Colors.text}
                    onClick={handleNavigate(2, ['Charging'])}
                  >
                    <IFText>{InfinityEnums.Analytics.CHARGING}</IFText>
                  </StyledHyperLink>
                  <IFText>
                    {connectorsStatus?.data?.connectorStatusCounts.charging}
                  </IFText>
                </div>
              </div>

              <div className={Styles.LegendDiv}>
                <div className={Styles.Legend}>
                  <div
                    className={Styles.Box}
                    style={{ backgroundColor: Colors.Analytics.Occupied }}
                  />
                  <StyledHyperLink
                    hoverColor={Colors.primary}
                    textColor={Colors.text}
                    onClick={handleNavigate(2, [
                      'Charging',
                      'SuspendedEV',
                      'SuspendedEVSE',
                    ])}
                  >
                    <IFText>{InfinityEnums.Analytics.OCCUPIED}</IFText>
                  </StyledHyperLink>
                  <IFText>
                    {connectorsStatus?.data?.connectorStatusCounts.occupied}
                  </IFText>
                </div>
                <div className={Styles.Legend}>
                  <div
                    className={Styles.Box}
                    style={{ backgroundColor: Colors.Analytics.Faulted }}
                  />
                  <StyledHyperLink
                    hoverColor={Colors.primary}
                    textColor={Colors.text}
                    onClick={handleNavigate(2, ['Faulted'])}
                  >
                    <IFText>{InfinityEnums.Analytics.FAULTED}</IFText>
                  </StyledHyperLink>
                  <IFText>
                    {connectorsStatus?.data?.connectorStatusCounts.faulted}
                  </IFText>
                </div>

                <div className={Styles.Last}>
                  <div
                    className={Styles.Box}
                    style={{ backgroundColor: Colors.Analytics.Offline }}
                  />
                  <StyledHyperLink
                    hoverColor={Colors.primary}
                    textColor={Colors.text}
                    onClick={handleNavigate(6, ['Offline'])}
                  >
                    <IFText>{InfinityEnums.Analytics.OFFLINE}</IFText>
                  </StyledHyperLink>
                  <IFText>
                    {connectorsStatus?.data?.connectorStatusCounts.offline}
                  </IFText>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  },
)

const mapStateToProps = (state) => ({
  connectorsStatus: AnalyticsSelectors.getConnectorsStatus(state),
  fetchConnectorsStatusRequestState:
    AnalyticsSelectors.getFetchConnectorsStatusRequestState(state),
})

const mapDispatchToProps = (dispatch) => ({
  fetchConnectorsStatus: () =>
    dispatch(AnalyticsActions.fetchConnectorsStatus()),
  setStationsFilter: (filter) =>
    dispatch(StationActions.setStationsFilter(filter)),
})

ConnectorsStatusCard.propTypes = {
  connectorsStatus: PropTypes.object,
  fetchConnectorsStatus: PropTypes.func,
  fetchConnectorsStatusRequestState: PropTypes.number,
  refreshComponentRef: PropTypes.func,
}

export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(ConnectorsStatusCard)
